import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const variantId = 50697757688048;
const optInKey = 'ruido-sticker-pack-enabled-v2';
let syncPromise = Promise.resolve();

const getCartUrl = () => (window.Theme?.routes?.cart_url || '/cart');
const getChangeUrl = () => window.Theme?.routes?.cart_change_url || '/cart/change';
const getAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add';
const moneyLabel = '$99 MXN';

function getSections() {
  return [...new Set([...document.querySelectorAll('cart-items-component')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean))];
}

async function readCart() {
  const response = await fetch(getCartUrl() + '.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('No se pudo leer el carrito.');
  return response.json();
}

function getProtectionItems(cart) {
  return (cart.items || []).filter((item) => Number(item.variant_id) === variantId);
}

function getMerchandiseQuantity(cart) {
  return (cart.items || [])
    .filter((item) => Number(item.variant_id) !== variantId && Number(item.variant_id) !== 50648164794608)
    .reduce((total, item) => total + Number(item.quantity || 0), 0);
}

function updateVisibleCartCount(cart) {
  const count = getMerchandiseQuantity(cart);
  document.querySelectorAll('[data-testid="cart-bubble"], [ref="cartBubbleCount"]').forEach((node) => {
    node.textContent = String(count);
    node.classList.toggle('hidden', count === 0);
  });
  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.toggle('visually-hidden', count === 0);
  });
  document.querySelectorAll('.theme-drawer__badge').forEach((badge) => {
    badge.textContent = String(count);
    badge.classList.toggle('visually-hidden', count === 0);
  });
}

function updateControls(enabled, message = '') {
  document.querySelectorAll('[data-ruido-sticker]').forEach((root) => {
    const toggle = root.querySelector('[data-ruido-sticker-toggle]');
    const coverage = root.querySelector('[data-ruido-sticker-coverage]');
    const status = root.querySelector('[data-ruido-sticker-status]');
    if (toggle) {
      toggle.checked = enabled;
      toggle.disabled = false;
    }
    if (coverage) coverage.hidden = !enabled;
    if (status) status.textContent = message;
  });
  document.querySelectorAll('button[data-ruido-sticker-lock]').forEach((button) => {
    button.disabled = false;
    button.removeAttribute('data-ruido-sticker-lock');
  });
}

function setBusy(message) {
  document.querySelectorAll('[data-ruido-sticker-toggle]').forEach((toggle) => {
    toggle.disabled = true;
  });
  document.querySelectorAll('#checkout, button[name="checkout"], .cart__checkout-button').forEach((button) => {
    if (!button.disabled) {
      button.disabled = true;
      button.setAttribute('data-ruido-sticker-lock', '');
    }
  });
  document.querySelectorAll('[data-ruido-sticker-status]').forEach((status) => {
    status.textContent = message;
  });
}

async function mutateProtection(add, cart) {
  const sections = getSections();
  if (!sections.length) throw new Error('No se encontró el bloque del carrito para actualizar.');
  const sectionData = { sections: sections.join(','), sections_url: window.location.pathname };
  const existing = getProtectionItems(cart);
  const lines = add
    ? [{ merchandiseId: String(variantId), quantity: 1 }]
    : existing.map((item) => ({ id: item.key, quantity: 0 }));
  if (!add && !lines.length) return cart;

  const deferred = CartLinesUpdateEvent.createPromise();
  const source = document.querySelector('[data-ruido-sticker]') || document;
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: add ? 'update' : 'remove',
    context: 'cart',
    lines,
    promise: deferred.promise,
  }));

  try {
    const url = add ? getAddUrl() : getChangeUrl();
    const payload = add
      ? { ...sectionData, items: [{ id: variantId, quantity: 1 }] }
      : { ...sectionData, id: existing[0].key, quantity: 0 };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.errors || 'No se pudo actualizar la pack de stickers.');
    }
    const latestCart = add ? await readCart() : result;
    const visibleCount = getMerchandiseQuantity(latestCart);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(latestCart),
      detail: {
        sections: result.sections || {},
        items: latestCart.items,
        itemCount: visibleCount,
        source: 'ruido-sticker-pack',
        didError: false,
      },
    });
    await Promise.all([...document.querySelectorAll('cart-items-component')]
      .map((component) => component.cartRenderComplete)
      .filter(Boolean));
    updateVisibleCartCount(latestCart);
    return latestCart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

async function syncProtection(cart) {
  if (!getMerchandiseQuantity(cart) && getProtectionItems(cart).length) {
    setBusy('Actualizando…');
    cart = await mutateProtection(false, cart);
  }
  updateVisibleCartCount(cart);
  updateControls(getProtectionItems(cart).length > 0);
}

document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-ruido-sticker-toggle]')) return;
  const enabled = event.target.checked;
  setBusy('Actualizando…');
  syncPromise = syncPromise.then(async () => {
    let cart = await readCart();
    if (enabled && !getMerchandiseQuantity(cart)) throw new Error('Agrega una prenda o disco primero.');
    if (enabled !== (getProtectionItems(cart).length > 0)) cart = await mutateProtection(enabled, cart);
    updateControls(getProtectionItems(cart).length > 0);
  }).catch(async () => {
    const cart = await readCart().catch(() => null);
    updateControls(cart ? getProtectionItems(cart).length > 0 : !enabled, 'No se pudo actualizar. Intenta de nuevo.');
  });
});

function queueSync(cart) {
  syncPromise = syncPromise
    .then(() => syncProtection(cart))
    .catch((error) => {
      updateControls(false, 'No se pudo actualizar la pack de stickers.');
      console.warn('[RUIDO pack de stickers]', error);
    });
  return syncPromise;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.target instanceof Element && event.target.closest('[data-ruido-sticker]')) return;
  event.promise?.then(({ detail }) => {
    if (!Array.isArray(detail?.items) || detail.didError) return;
    requestAnimationFrame(() => queueSync({ items: detail.items }));
  }).catch(() => {});
});

